import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toggleModalWorkspaceCreate } from '../../../redux/main/main.actions';
import { selectIsModalWorkspaceCreateEnabled } from '../../../redux/main/main.selectors';
import { workspaceCreateRequest } from '../../../redux/workspace/workspace.actions';

import Modal from '../Modal/Modal';

import './ModalWorkspaceCreateNew.scss';

// Floor on loader visibility; also must exceed the 180ms loader-enter delay
// so the post-loading transition's setLoading(false) wins the race.
const MIN_LOADING_MS = 500;

export const ModalWorkspaceCreateNew = () => {
    const dispatch = useDispatch();

    const isOpen = useSelector(selectIsModalWorkspaceCreateEnabled);

    const [step, setStep] = useState(1);
    const [direction, setDirection] = useState('forward');
    const [animating, setAnimating] = useState(false);
    const [displayStep, setDisplayStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [workspaceName, setWorkspaceName] = useState('My Workspace');

    const inputRef = useRef(null);
    const loadingStartedAtRef = useRef(0);

    const handleRender = () => {
        if (isOpen && step === 1 && inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleCancel = () => {
        if (loading) return;
        dispatch(toggleModalWorkspaceCreate(null));
        setTimeout(() => {
            setStep(1);
            setDisplayStep(1);
            setDirection('forward');
            setAnimating(false);
            setLoading(false);
            setWorkspaceName('My Workspace');
        }, 300);
    };

    const handleCreateWorkspace = () => {
        if (loading) return;
        loadingStartedAtRef.current = Date.now();

        // Transition into the loading step (unchanged animation timing).
        setDirection('forward');
        setAnimating(true);
        setStep('loading');
        setTimeout(() => {
            setDisplayStep('loading');
            setAnimating(false);
            setLoading(true);
        }, 180);

        const remainingLoaderMs = () => Math.max(0, MIN_LOADING_MS - (Date.now() - loadingStartedAtRef.current));

        dispatch(workspaceCreateRequest({
            title: workspaceName.trim(),
            email: '',                          // no invite step — saga ignores empty
            onSuccess: () => {
                setTimeout(() => {
                    setLoading(false);
                    handleCancel();
                }, remainingLoaderMs());
            },
            onError: (info) => {
                setTimeout(() => {
                    // 402: saga has already opened ModalPricingPlanLimit. Close ourselves
                    // so the limit modal owns the UX.
                    if (info?.kind === 'plan_limit') {
                        setLoading(false);
                        handleCancel();
                        return;
                    }
                    // 401: re-auth flow takes over. Close to avoid stranding the user
                    // on a frozen wizard behind the login redirect.
                    if (info?.kind === 'auth') {
                        setLoading(false);
                        handleCancel();
                        return;
                    }
                    // Validation / generic: return to step 1 so the user can adjust.
                    setLoading(false);
                    setDirection('backward');
                    setAnimating(true);
                    setStep(1);
                    setTimeout(() => {
                        setDisplayStep(1);
                        setAnimating(false);
                    }, 180);
                }, remainingLoaderMs());
            },
        }));
    };

    const stepClass = step === 'loading' ? 'step-loading' : `step-${step}`;

    const animClass = animating
        ? `step-exit-${direction}`
        : `step-enter-${direction}`;

    return (
        <Modal
            isOpen={isOpen}
            modalClass={`modal-workspace-create ${stepClass}`}
            onCancel={handleCancel}
            onShouldRender={handleRender}
        >
            <div className={`step-content ${animClass}`}>
                {displayStep === 1 && (
                    <>
                        <div className="header">Create a new workspace</div>
                        <div className="form-group one">
                            <div className="description-text">Workspace name</div>
                            <input
                                className='input-with-role-input'
                                maxLength="1000"
                                ref={inputRef}
                                autoComplete="off"
                                spellCheck="false"
                                type="text"
                                id="diagram-name"
                                placeholder="Enter workspace name"
                                value={workspaceName}
                                onChange={(e) => setWorkspaceName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (workspaceName.trim()) handleCreateWorkspace();
                                    }
                                }}
                            />
                        </div>
                        <button
                            onClick={handleCreateWorkspace}
                            disabled={!workspaceName.trim()}
                        >Create workspace</button>
                    </>
                )}

                {displayStep === 'loading' && (
                    <div className="loading-step">
                        <div className="spinner"></div>
                        <div className="loading-text">Creating your workspace...</div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ModalWorkspaceCreateNew;
